import type { Invoice, StudentStatementReport } from "../../services/financialService";

const sum = (values: Array<string | number>) => values.reduce<number>((total, value) => total + Number(value || 0), 0);

// Drafts are not yet owed and cancelled invoices never will be.
const isLive = (invoice: Invoice) => !["draft", "cancelled"].includes(invoice.status);

/** One learner's account at a glance, from their statement and invoices.
 *
 *  The statement is the authority on what is owed (it runs to the balance the
 *  bursar would quote); the invoices add what the statement does not carry --
 *  due dates and status -- so the record can say what is overdue and what falls
 *  due next. */
export function summariseStudentAccount(
  statement: StudentStatementReport | undefined,
  invoices: Invoice[] = [],
  today: string,
) {
  const events = statement?.events ?? [];
  const receipts = statement?.receipts ?? [];
  const ofType = (type: string, side: "debit" | "credit") =>
    sum(events.filter((event) => event.type === type).map((event) => event[side]));

  const billed = ofType("charge", "debit");
  const discounts = ofType("discount", "credit");
  const cancelled = ofType("cancellation", "credit");
  const credited = ofType("credit_note", "credit");
  const refunded = ofType("refund", "debit");
  // Every receipt, including money held as credit that has not yet been
  // applied to an invoice. Older statements without receipts fall back to
  // what was applied.
  const paid = receipts.length
    ? sum(receipts.map((receipt) => Number(receipt.amount) - Number(receipt.refunded)))
    : ofType("payment", "credit");

  const open = invoices.filter((invoice) => isLive(invoice) && Number(invoice.balance_due) > 0);
  const overdue = open.filter((invoice) => invoice.due_date && invoice.due_date < today);
  const upcoming = open
    .filter((invoice) => invoice.due_date && invoice.due_date >= today)
    .sort((a, b) => a.due_date.localeCompare(b.due_date));

  return {
    billed,
    discounts,
    cancelled,
    credited,
    refunded,
    paid,
    outstanding: Number(statement?.outstanding_balance ?? 0),
    creditHeld: Number(statement?.credit_balance ?? 0),
    balanceAfterCredit:
      statement?.balance_after_credit !== undefined ? Number(statement.balance_after_credit) : null,
    invoiceCount: invoices.filter(isLive).length,
    openCount: open.length,
    overdueCount: overdue.length,
    overdueAmount: sum(overdue.map((invoice) => invoice.balance_due)),
    nextDue: upcoming[0] ?? null,
    lastReceipt: receipts.length ? receipts[receipts.length - 1] : null,
    hasActivity: Boolean(events.length || receipts.length || invoices.length),
  };
}

/** How an invoice stands for the reader, including "overdue" the server has not yet stamped. */
export function invoiceStanding(invoice: Invoice, today: string) {
  if (isLive(invoice) && Number(invoice.balance_due) > 0 && invoice.due_date && invoice.due_date < today) {
    return "overdue";
  }
  return invoice.status;
}

export const INVOICE_STANDING_DOTS: Record<string, string> = {
  draft: "bg-gray-400",
  sent: "bg-blue-500",
  paid: "bg-green-500",
  overdue: "bg-red-500",
  credited: "bg-purple-500",
  cancelled: "bg-gray-300",
};

export const STATEMENT_EVENT_LABELS: Record<string, string> = {
  charge: "Invoice",
  discount: "Discount",
  cancellation: "Cancellation",
  payment: "Payment",
  refund: "Refund",
  credit_note: "Credit note",
};
